import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { useUploadLabReport } from './useLab';
import { validateReportFile, type ReportRejection } from './labClient';
import type { LabOrderDetail } from '@/types/lab';

interface ReportUploaderProps {
  orderId: string;
  testId: string;
  /** The document being replaced, or null for a first upload. */
  supersedesDocumentId: string | null;
  onUploaded: (order: LabOrderDetail, duplicateOf: string | null) => void;
  disabled?: boolean;
}

const REJECTION_KEY: Record<ReportRejection, string> = {
  'not-pdf': 'lab.detail.notPdf',
  'too-large': 'lab.detail.tooLarge',
  empty: 'lab.detail.emptyFile',
};

/**
 * Attach one report PDF to one test.
 *
 * Three things here are deliberate and easy to lose in a refactor:
 *
 *  - **The file is checked before any network call.** A 20 MiB upload that dies
 *    on the confirm step is a miserable experience on a clinic's 3G tail, and the
 *    magic-byte read is what catches a `.docx` renamed to `.pdf` — `file.type`
 *    reports what the OS guessed from the extension.
 *  - **There is no optimistic update.** A report that appears attached and then
 *    turns out not to be is worse than a spinner.
 *  - **A 409 is offered as a choice, not swallowed.** It means a colleague
 *    uploaded while this upload was in flight; replacing keeps both on the
 *    record, so the user gets to decide rather than having one silently win.
 */
export function ReportUploader({
  orderId,
  testId,
  supersedesDocumentId,
  onUploaded,
  disabled = false,
}: ReportUploaderProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  /**
   * Set only when the server reported a 409. Its `currentDocumentId` may itself
   * be null (the report was removed rather than replaced while we uploaded), so
   * presence of this object — not the id inside it — is what means "in
   * conflict". Bundling the retained file with it keeps the two from drifting
   * apart into a state where one is set and the other is not.
   */
  const [conflict, setConflict] = useState<
    { currentDocumentId: string | null; file: File } | null
  >(null);

  const upload = useUploadLabReport();

  async function start(file: File, supersedes: string | null) {
    setError(null);
    setConflict(null);

    const rejection = await validateReportFile(file);
    if (rejection) {
      setError(t(REJECTION_KEY[rejection]));
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setProgress(0);

    try {
      const { order, duplicateOf } = await upload.mutateAsync({
        orderId,
        testId,
        file,
        supersedesDocumentId: supersedes,
        signal: controller.signal,
        onProgress: ({ loaded, total }) =>
          setProgress(total > 0 ? Math.round((loaded / total) * 100) : 0),
      });
      onUploaded(order, duplicateOf);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'UPLOAD_ABORTED') {
        // The user cancelled; that is not an error to report back at them.
        return;
      }
      if (err instanceof ApiError && err.code === 'CONFLICT') {
        // Someone else got there first. Keep the file so "Replace anyway" does
        // not make the user find it again.
        const current = (err.details as { currentDocumentId?: string } | undefined)
          ?.currentDocumentId;
        setConflict({ currentDocumentId: current ?? null, file });
        return;
      }
      setError(translateApiError(err, { fallback: t('lab.detail.uploadFailed') }));
    } finally {
      abortRef.current = null;
      setProgress(null);
    }
  }

  const busy = progress !== null;

  if (conflict) {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-warn bg-paper-2 px-3 py-2.5">
        <p className="text-[13px] font-medium text-ink">{t('lab.detail.conflictTitle')}</p>
        <p className="text-[12.5px] text-muted">{t('lab.detail.conflictExplain')}</p>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => void start(conflict.file, conflict.currentDocumentId)}
          >
            {t('lab.detail.replaceAnyway')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setConflict(null)}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="sr-only"
        aria-label={t('lab.detail.chooseFile')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so choosing the same file twice still fires a change event —
          // which matters after a failed upload the user wants to retry.
          e.target.value = '';
          if (file) void start(file, supersedesDocumentId);
        }}
      />

      {busy ? (
        <div className="flex items-center gap-2">
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-full bg-paper-2"
            role="progressbar"
            aria-valuenow={progress ?? 0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('lab.detail.uploading')}
          >
            <div className="h-full bg-ink transition-[width]" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-[12px] tabular-nums text-muted">{progress}%</span>
          <button
            type="button"
            onClick={() => abortRef.current?.abort()}
            // Full-height target: this is the only way to stop a 20 MiB upload
            // that is going to the wrong test, and on a phone a 16px underline
            // is not a control anyone can hit in a hurry.
            className="inline-flex min-h-[44px] items-center px-2 text-[12px] text-muted underline hover:text-ink"
          >
            {t('lab.detail.uploadCancel')}
          </button>
        </div>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="gap-1.5 self-start"
        >
          <Icon name="arrow_up_right" size={14} />
          {supersedesDocumentId ? t('lab.detail.replaceReport') : t('lab.detail.uploadReport')}
        </Button>
      )}

      {/* Announced: the upload control is small and the operator's attention is
          on the tube, not the screen. A failure they do not notice is a report
          they believe is filed. */}
      {error && (
        <p role="alert" className="text-[12.5px] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
